import { t as tCem } from '../utils/i18n'
/**
 * ReportsView — KPIs, reportes de turno y tendencias
 */

import { useState, useEffect, useCallback } from 'react'
import {
  AlertTriangle, AlertOctagon, Clock, Car, Server, MapPin,
  BarChart3, ClipboardList, TrendingUp, RefreshCw, Download, Loader2,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMins(mins) {
  if (mins === null || mins === undefined) return '—'
  if (mins < 60) return `${Math.round(mins)}m`
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`
}

function fmtNum(n) {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString('es-CL')
}

// ─── Mini Bar Chart (SVG inline) ─────────────────────────────────────────────

function MiniBarChart({ data, keys, colors, height = 100 }) {
  if (!data?.length) return null
  const maxVal = Math.max(...data.flatMap(d => keys.map(k => d[k] || 0)), 1)
  const barW = Math.max(4, Math.floor(320 / data.length) - 2)

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${data.length * (barW + 2)} ${height}`} preserveAspectRatio="none">
      {data.map((d, i) => {
        let yOffset = 0
        return keys.map((k, ki) => {
          const val = d[k] || 0
          const barH = Math.max(0, (val / maxVal) * (height - 20))
          const x = i * (barW + 2)
          const y = height - barH - 2
          const rect = (
            <rect key={`${k}-${i}`} x={x} y={y} width={barW} height={barH}
              fill={colors[ki]} rx={2} opacity={0.85} />
          )
          yOffset += barH
          return rect
        })
      })}
    </svg>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KPICard({ title, value, sub, icon, color = 'var(--accent)', trend }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '16px 20px',
      display: 'flex', flexDirection: 'column', gap: 6,
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {title}
        </span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  )
}

// ─── Severity badge ───────────────────────────────────────────────────────────

const SEV_COLORS = {
  critical: { bg: '#F3E8FF', color: '#6B21A8' },
  high:     { bg: 'var(--danger-light)', color: 'var(--danger-text)' },
  medium:   { bg: 'var(--warning-light)', color: 'var(--warning-text)' },
  low:      { bg: 'var(--success-light)', color: 'var(--success-text)' },
}

function SevBadge({ sev }) {
  const c = SEV_COLORS[sev] || SEV_COLORS.low
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: c.bg, color: c.color, textTransform: 'uppercase',
    }}>
      {sev}
    </span>
  )
}

// ─── ReportsView ──────────────────────────────────────────────────────────────

const _rvEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
const WINDOWS = _rvEN ? [
  { label: 'Last hour',  hours: 1 },
  { label: 'Last 8h',    hours: 8 },
  { label: 'Last 24h',   hours: 24 },
  { label: 'Last 7d',    hours: 168 },
] : [
  { label: 'Última hora',  hours: 1 },
  { label: 'Últimas 8h',   hours: 8 },
  { label: 'Últimas 24h',  hours: 24 },
  { label: 'Últimos 7d',   hours: 168 },
]

export default function ReportsView() {
  const token = useAuthStore(s => s.token)
  const [hours,   setHours]   = useState(8)
  const [kpis,    setKpis]    = useState(null)
  const [shift,   setShift]   = useState(null)
  const [trends,  setTrends]  = useState(null)
  const [bySite,  setBySite]  = useState(null)
  const [loading, setLoading] = useState(false)
  const [tab,     setTab]     = useState('kpis')

  const headers = { Authorization: `Bearer ${token}` }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [kpisR, shiftR, trendsR, bySiteR] = await Promise.all([
        fetch(`/api/reports/kpis?hours=${hours}`,     { headers }).then(r => r.ok ? r.json() : null),
        fetch(`/api/reports/shift?hours=${hours}`,    { headers }).then(r => r.ok ? r.json() : null),
        fetch(`/api/reports/trends?days=7`,           { headers }).then(r => r.ok ? r.json() : null),
        fetch(`/api/reports/by-site?hours=${hours}`,  { headers }).then(r => r.ok ? r.json() : null),
      ])
      setKpis(kpisR)
      setShift(shiftR)
      setTrends(trendsR)
      setBySite(bySiteR)
    } finally {
      setLoading(false)
    }
  }, [hours, token])

  useEffect(() => { load() }, [load])

  const exportShift = () => {
    if (!shift) return
    const lines = [
      `REPORTE DE TURNO — ${new Date().toLocaleString('es-CL')}`,
      `Período: ${new Date(shift.period.from).toLocaleString('es-CL')} → ${new Date(shift.period.to).toLocaleString('es-CL')} (${shift.period.hours}h)`,
      '',
      `INCIDENTES: ${shift.incidents.total}`,
      `  Críticos: ${shift.incidents.by_severity.critical}`,
      `  Altos:    ${shift.incidents.by_severity.high}`,
      `  Medios:   ${shift.incidents.by_severity.medium}`,
      `  Bajos:    ${shift.incidents.by_severity.low}`,
      `  Resueltos: ${shift.incidents.resolved}`,
      `  T. medio respuesta: ${fmtMins(shift.incidents.avg_resolution_min)}`,
      '',
      `ANPR DETECTIONS: ${shift.lpr.total} (${shift.lpr.per_hour}/h)`,
      '',
      'INCIDENTES CRÍTICOS/ALTOS:',
      ...(shift.incidents.critical_list.map(i =>
        `  [${i.severity.toUpperCase()}] #${i.id} ${i.title} — ${i.site_name} — ${i.status}`
      )),
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reporte_turno_${new Date().toISOString().slice(0,16).replace('T','_')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        padding: '12px 20px', background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        {/* Ventana de tiempo */}
        <div style={{ display: 'flex', gap: 4 }}>
          {WINDOWS.map(w => (
            <button
              key={w.hours}
              onClick={() => setHours(w.hours)}
              style={{
                padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
                background: hours === w.hours ? 'var(--accent)' : 'transparent',
                color: hours === w.hours ? '#fff' : 'var(--text-secondary)',
                fontWeight: hours === w.hours ? 600 : 400,
              }}
            >
              {w.label}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginLeft: 12 }}>
          {(_rvEN
            ? [['kpis', <><BarChart3 size={12}/> KPIs</>], ['shift', <><ClipboardList size={12}/> Shift</>], ['trends', <><TrendingUp size={12}/> Trends</>], ['sites', <><MapPin size={12}/> By site</>]]
            : [['kpis', <><BarChart3 size={12}/> KPIs</>], ['shift', <><ClipboardList size={12}/> Turno</>], ['trends', <><TrendingUp size={12}/> Tendencias</>], ['sites', <><MapPin size={12}/> Por sitio</>]]
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                border: 'none', cursor: 'pointer', fontSize: 12,
                background: tab === id ? 'var(--bg-muted)' : 'transparent',
                color: tab === id ? 'var(--text-primary)' : 'var(--text-muted)',
                fontWeight: tab === id ? 600 : 400,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <button
          onClick={load}
          disabled={loading}
          style={{
            marginLeft: 'auto', padding: '5px 12px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)', background: 'transparent',
            color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
          }}
        >
          {loading ? <Loader2 size={12} style={{animation:'spin 1s linear infinite'}}/> : <RefreshCw size={12}/>} Refresh
        </button>

        {tab === 'shift' && (
          <button
            onClick={exportShift}
            style={{
              padding: '5px 12px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
            }}
          >
            <Download size={12}/> {tCem('Exportar turno','Export shift')}
          </button>
        )}
      </div>

      {/* ── Contenido ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 13 }}>
            {tCem('Cargando datos…','Loading data…')}
          </div>
        )}

        {/* ── KPIs ──────────────────────────────────────────────────────── */}
        {!loading && tab === 'kpis' && kpis && (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 12, marginBottom: 24,
            }}>
              <KPICard
                icon={<AlertTriangle size={14}/>}
                title="Total incidents"
                value={fmtNum(kpis.incidents.total)}
                sub={`${kpis.incidents.open} open · ${kpis.incidents.resolved} resolved`}
                color="var(--warning)"
              />
              <KPICard
                icon={<AlertOctagon size={14}/>}
                title="Críticos + Altos"
                value={fmtNum(kpis.incidents.critical + kpis.incidents.high)}
                sub={`${kpis.incidents.critical} critical, ${kpis.incidents.high} high`}
                color="#dc2626"
              />
              <KPICard
                icon={<Clock size={14}/>}
                title="T. medio resolución"
                value={fmtMins(kpis.incidents.avg_resolution_min)}
                sub="Promedio del período"
                color="var(--accent)"
              />
              <KPICard
                icon={<Car size={14}/>}
                title="Detecciones LPR"
                value={fmtNum(kpis.lpr.total_detections)}
                sub={`${kpis.lpr.per_hour}/hour avg`}
                color="#059669"
              />
              <KPICard
                icon={<Server size={14}/>}
                title="Nodos edge online"
                value={`${kpis.edge_nodes.online}/${kpis.edge_nodes.total}`}
                sub={kpis.edge_nodes.offline > 0 ? `⚠ ${kpis.edge_nodes.offline} offline` : '✓ Todos online'}
                color={kpis.edge_nodes.offline > 0 ? '#dc2626' : '#059669'}
              />
              <KPICard
                icon={<MapPin size={14}/>}
                title={tCem('Sitios con alarma','Sites with alarm')}
                value={fmtNum(kpis.sites_with_alarm)}
                sub="Active incidents"
                color={kpis.sites_with_alarm > 0 ? '#dc2626' : '#059669'}
              />
            </div>

            {/* Desglose de severidades */}
            {shift && (
              <div style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 20, marginBottom: 16,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 16 }}>
                  Incidents by severity
                </div>
                <div style={{ display: 'flex', gap: 24 }}>
                  {Object.entries(shift.incidents.by_severity).map(([sev, count]) => (
                    <div key={sev} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: SEV_COLORS[sev]?.color }}>
                        {count}
                      </div>
                      <SevBadge sev={sev} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Turno ────────────────────────────────────────────────────── */}
        {!loading && tab === 'shift' && shift && (
          <div>
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 12,
              color: 'var(--text-secondary)',
            }}>
              <ClipboardList size={12} style={{display:'inline',verticalAlign:'middle',marginRight:4}}/> Turno: <strong>{new Date(shift.period.from).toLocaleString('es-CL')}</strong> →{' '}
              <strong>{new Date(shift.period.to).toLocaleString('es-CL')}</strong>{' '}
              ({shift.period.hours}h)
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Resumen incidentes */}
              <div style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 16,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display:'flex', alignItems:'center', gap:6 }}><AlertTriangle size={13}/> Incidentes</div>
                {[
                  ['Total', shift.incidents.total],
                  ['Críticos', shift.incidents.by_severity.critical],
                  ['Altos', shift.incidents.by_severity.high],
                  ['Medios', shift.incidents.by_severity.medium],
                  ['Bajos', shift.incidents.by_severity.low],
                  ['Resueltos en turno', shift.incidents.resolved],
                  ['T. medio resolución', fmtMins(shift.incidents.avg_resolution_min)],
                ].map(([label, val]) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <strong>{val}</strong>
                  </div>
                ))}
              </div>

              {/* Sitios más activos */}
              <div style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 16,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display:'flex', alignItems:'center', gap:6 }}><MapPin size={13}/> Top sitios</div>
                {shift.incidents.top_sites.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: 8 }}>
                    Sin incidentes en el período
                  </div>
                )}
                {shift.incidents.top_sites.map((s, i) => (
                  <div key={s.site_id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
                  }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'var(--bg-muted)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                      flexShrink: 0,
                    }}>{i + 1}</span>
                    <span style={{ flex: 1 }}>{s.site_name}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 7px',
                      borderRadius: 10, background: 'var(--warning-light)',
                      color: 'var(--warning-text)',
                    }}>
                      {s.incidents}
                    </span>
                  </div>
                ))}
                {shift.lpr.total > 0 && (
                  <div style={{
                    marginTop: 12, padding: '8px 0', fontSize: 12,
                    color: 'var(--text-secondary)', borderTop: '1px solid var(--border)',
                  }}>
                    <Car size={12} style={{display:'inline',verticalAlign:'middle',marginRight:4}}/> LPR: <strong>{fmtNum(shift.lpr.total)}</strong> detecciones
                    ({shift.lpr.per_hour}/h)
                  </div>
                )}
              </div>
            </div>

            {/* Incidentes críticos */}
            {shift.incidents.critical_list.length > 0 && (
              <div style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 16,
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display:'flex', alignItems:'center', gap:6 }}>
                  <AlertOctagon size={13}/> Incidentes críticos y high del turno
                </div>
                {shift.incidents.critical_list.map(inc => (
                  <div key={inc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
                  }}>
                    <SevBadge sev={inc.severity} />
                    <span style={{ flex: 1, fontWeight: 500 }}>{inc.title}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{inc.site_name}</span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 6,
                      background: 'var(--bg-muted)', color: 'var(--text-muted)',
                    }}>
                      {inc.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tendencias ────────────────────────────────────────────────── */}
        {!loading && tab === 'trends' && trends && (
          <div>
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 10, padding: 20, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display:'flex', alignItems:'center', gap:6 }}>
                <TrendingUp size={13}/> Incidentes últimos 7 días
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
                Azul = incidentes · Rojo = críticos
              </div>
              <MiniBarChart
                data={trends.data}
                keys={['incidents', 'critical']}
                colors={['#3b82f6', '#dc2626']}
                height={120}
              />
              <div style={{
                display: 'flex', gap: 0, marginTop: 8,
                overflowX: 'auto',
              }}>
                {trends.data.map(d => (
                  <div key={d.date} style={{
                    flex: 1, textAlign: 'center', fontSize: 9,
                    color: 'var(--text-muted)', minWidth: 30,
                  }}>
                    {d.date.slice(8)}
                  </div>
                ))}
              </div>
            </div>

            {/* Tabla de datos */}
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 10, overflow: 'hidden',
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px',
                padding: '8px 16px', background: 'var(--bg-muted)',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                color: 'var(--text-muted)', letterSpacing: '0.04em',
              }}>
                <span>Fecha</span>
                <span style={{ textAlign: 'right' }}>Incidentes</span>
                <span style={{ textAlign: 'right' }}>Críticos</span>
                <span style={{ textAlign: 'right' }}>LPR</span>
              </div>
              {[...trends.data].reverse().map(d => (
                <div key={d.date} style={{
                  display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px',
                  padding: '8px 16px', borderBottom: '1px solid var(--border)',
                  fontSize: 12,
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {new Date(d.date + 'T12:00:00Z').toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <span style={{ textAlign: 'right', fontWeight: d.incidents > 0 ? 600 : 400 }}>
                    {d.incidents}
                  </span>
                  <span style={{
                    textAlign: 'right',
                    color: d.critical > 0 ? '#dc2626' : 'var(--text-muted)',
                    fontWeight: d.critical > 0 ? 700 : 400,
                  }}>
                    {d.critical || '—'}
                  </span>
                  <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                    {fmtNum(d.lpr)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Por sitio ─────────────────────────────────────────────────── */}
        {!loading && tab === 'sites' && bySite && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px',
              padding: '8px 16px', background: 'var(--bg-muted)',
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: 'var(--text-muted)', letterSpacing: '0.04em',
              position: 'sticky', top: 0,
            }}>
              <span>Sitio</span>
              <span style={{ textAlign: 'right' }}>Incidentes</span>
              <span style={{ textAlign: 'right' }}>Críticos</span>
              <span style={{ textAlign: 'right' }}>Abiertos</span>
              <span style={{ textAlign: 'right' }}>LPR</span>
            </div>
            {bySite.sites.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                Sin actividad en el período
              </div>
            )}
            {bySite.sites.map(s => (
              <div key={s.site_id} style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px',
                padding: '10px 16px', borderBottom: '1px solid var(--border)',
                fontSize: 12, alignItems: 'center',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{s.site_name}</div>
                  {s.address && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.address}</div>}
                </div>
                <span style={{ textAlign: 'right', fontWeight: s.incidents > 0 ? 600 : 400 }}>
                  {s.incidents}
                </span>
                <span style={{
                  textAlign: 'right',
                  color: s.critical > 0 ? '#dc2626' : 'var(--text-muted)',
                  fontWeight: s.critical > 0 ? 700 : 400,
                }}>
                  {s.critical || '—'}
                </span>
                <span style={{
                  textAlign: 'right',
                  color: s.open > 0 ? 'var(--warning-text)' : 'var(--text-muted)',
                }}>
                  {s.open || '—'}
                </span>
                <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmtNum(s.lpr) || '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
